import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

export function isIPhone({ userAgent }: UtilityContext): boolean {
	return ['iPhone'].includes(userAgent.getParsedUserAgent().getDevice().model || '');
}

export function isIPhoneOrIPad({ userAgent }: UtilityContext): boolean {
	return ['iPhone', 'iPad'].includes(userAgent.getParsedUserAgent().getDevice().model || '');
}

export function isAndroidPhone({ userAgent }: UtilityContext): boolean {
	return (
		userAgent.getParsedUserAgent().getDevice().type === 'mobile' &&
		userAgent.getParsedUserAgent().getOS().name === 'Android'
	);
}

export function isChrome({ userAgent }: UtilityContext): boolean {
	return userAgent.getParsedUserAgent().getBrowser().name === 'Chrome';
}

export function isAndroidChrome(utilityContext: UtilityContext): boolean {
	return isAndroidPhone(utilityContext) && isChrome(utilityContext);
}

export function isAndroidWebview({ userAgent }: UtilityContext): boolean {
	return userAgent.getParsedUserAgent().getOS().name === 'Android' && userAgent.isWebview;
}

export function isSafari({ userAgent }: UtilityContext): boolean {
	return ['Safari', 'Mobile Safari'].includes(userAgent.getParsedUserAgent().getBrowser().name || '');
}

export function isSamsungPhone(utilityContext: UtilityContext): boolean {
	const { userAgent } = utilityContext;
	return isAndroidPhone(utilityContext) && userAgent.getParsedUserAgent().getDevice().vendor === 'Samsung';
}

export function isMobile({ userAgent }: UtilityContext): boolean {
	return ['mobile', 'tablet'].includes(userAgent.getParsedUserAgent().getDevice().type || '');
}

/**
 * Phones only — unlike `isMobile()`, which also matches tablets.
 *
 * Prefer this over `isMobile()` when gating something the `md` breakpoint (768px) hides, e.g. the
 * petition-details mobile action-button tree (`block md:hidden`): a tablet is a "mobile" user agent
 * but typically sits at or above 768px CSS width, so it never renders that tree.
 *
 * Device type is an approximation of viewport width, not a substitute for it. A phone in landscape
 * can exceed 768px and still pass this check, and a small tablet under 768px will fail it despite
 * rendering the subtree. Use it where the exact viewport isn't knowable — server-side, or before
 * hydration — and accept the residual mismatch; it is far closer than `isMobile()`, which admits
 * every tablet. If you need the real breakpoint, measure it client-side instead.
 */
export function isPhone({ userAgent }: UtilityContext): boolean {
	return userAgent.getParsedUserAgent().getDevice().type === 'mobile';
}
